import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import {
  AUTO_SAVE_INTERVAL,
  LEVEL_CONFIG,
  SCORE_CONFIG,
  STAR_CONFIG,
} from "../constants";
import { MathZooAPI } from "../services/api";

// localStorage keys
const STORAGE_KEYS = {
  USER: "mathzoo_user",
  PROGRESS: "mathzoo_progress",
  SESSION: "mathzoo_session",
};

// 從 localStorage 加載已保存的狀態
const loadSavedState = () => {
  try {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const savedProgress = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    const savedSession = localStorage.getItem(STORAGE_KEYS.SESSION);

    if (savedUser && savedSession) {
      const user = JSON.parse(savedUser);
      const progress = savedProgress ? JSON.parse(savedProgress) : null;
      return { user, progress, isLoggedIn: true };
    }
  } catch (error) {
    console.error("Failed to load saved state:", error);
  }
  return null;
};

// 保存狀態到 localStorage
const saveStateToStorage = (user, progress) => {
  try {
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.SESSION, Date.now().toString());
      if (progress) {
        localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(progress));
      }
    }
  } catch (error) {
    console.error("Failed to save state to storage:", error);
  }
};

// 清除 localStorage
const clearStorageState = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.SESSION);
  } catch (error) {
    console.error("Failed to clear storage:", error);
  }
};

// 初始狀態
const initialState = {
  // 用戶信息
  user: null,
  isLoggedIn: false,

  // 全局進度
  totalScore: 0,
  totalStars: 0,
  streak: 0,

  // 各遊戲進度
  gameProgress: {
    decomposition: {
      level: 1,
      correctCount: 0,
      totalPlayed: 0,
      highScore: 0,
    },
    placeValue: {
      level: 1,
      correctCount: 0,
      totalPlayed: 0,
      highScore: 0,
    },
    multiples: {
      level: 1,
      correctCount: 0,
      totalPlayed: 0,
      highScore: 0,
    },
  },

  // 當前遊戲狀態
  currentGame: null,
  currentQuestion: null,
  hintsUsed: 0,
  showHint: false,

  // UI 狀態
  showLevelUp: false,
  showSuccess: false,
  showError: false,
  feedback: null,

  // 遊戲歷史
  gameHistory: [],

  // 解鎖的動物
  unlockedAnimals: ["rabbit", "bear", "frog"],

  // 系統狀態
  isLoading: false,
  error: null,
  lastSaved: null,
};

// Action types
const ACTION_TYPES = {
  // 用戶操作
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",

  // 遊戲操作
  START_GAME: "START_GAME",
  END_GAME: "END_GAME",
  SET_QUESTION: "SET_QUESTION",

  // 答題操作
  ANSWER_CORRECT: "ANSWER_CORRECT",
  ANSWER_WRONG: "ANSWER_WRONG",
  USE_HINT: "USE_HINT",
  HIDE_HINT: "HIDE_HINT",

  // 進度操作
  LEVEL_UP: "LEVEL_UP",
  HIDE_LEVEL_UP: "HIDE_LEVEL_UP",
  UPDATE_STREAK: "UPDATE_STREAK",

  // UI 操作
  SHOW_FEEDBACK: "SHOW_FEEDBACK",
  HIDE_FEEDBACK: "HIDE_FEEDBACK",
  CLEAR_RESULT: "CLEAR_RESULT",

  // 數據操作
  LOAD_PROGRESS: "LOAD_PROGRESS",
  SAVE_SUCCESS: "SAVE_SUCCESS",
  SET_ERROR: "SET_ERROR",
  SET_LOADING: "SET_LOADING",

  // 成就操作
  UNLOCK_ANIMAL: "UNLOCK_ANIMAL",

  // 用戶資料操作
  UPDATE_USER: "UPDATE_USER",
};

// Reducer
function gameReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.LOGIN:
      // 保存用戶信息到 localStorage
      saveStateToStorage(action.payload, null);
      return {
        ...state,
        user: action.payload,
        isLoggedIn: true,
      };

    case ACTION_TYPES.LOGOUT:
      // 清除 localStorage
      clearStorageState();
      return {
        ...initialState,
      };

    case ACTION_TYPES.START_GAME:
      return {
        ...state,
        currentGame: action.payload,
        hintsUsed: 0,
        showHint: false,
      };

    case ACTION_TYPES.END_GAME:
      return {
        ...state,
        currentGame: null,
        currentQuestion: null,
        hintsUsed: 0,
        showHint: false,
      };

    case ACTION_TYPES.SET_QUESTION:
      return {
        ...state,
        currentQuestion: action.payload,
        hintsUsed: 0,
        showHint: false,
        showSuccess: false,
        showError: false,
      };

    case ACTION_TYPES.ANSWER_CORRECT: {
      const { gameType, usedHint } = action.payload;
      const currentProgress = state.gameProgress[gameType];
      const newCorrectCount = currentProgress.correctCount + 1;
      const newTotalPlayed = currentProgress.totalPlayed + 1;

      // 計算得分
      const baseScore = SCORE_CONFIG.CORRECT_ANSWER;
      const streakBonus = state.streak >= 3 ? SCORE_CONFIG.STREAK_BONUS : 0;
      const hintPenalty = usedHint ? SCORE_CONFIG.HINT_PENALTY : 0;
      const scoreGain = Math.max(0, baseScore + streakBonus - hintPenalty);

      // 計算星星
      const starGain = usedHint
        ? STAR_CONFIG.CORRECT_ANSWER
        : STAR_CONFIG.PERFECT_ANSWER;

      // 檢查是否升級
      const shouldLevelUp =
        newCorrectCount % LEVEL_CONFIG.QUESTIONS_PER_LEVEL === 0;
      const newLevel = shouldLevelUp
        ? currentProgress.level + 1
        : currentProgress.level;

      return {
        ...state,
        totalScore:
          state.totalScore +
          scoreGain +
          (shouldLevelUp ? SCORE_CONFIG.LEVEL_UP_BONUS : 0),
        totalStars:
          state.totalStars +
          starGain +
          (shouldLevelUp ? STAR_CONFIG.LEVEL_UP_BONUS : 0),
        streak: state.streak + 1,
        showSuccess: true,
        showLevelUp: shouldLevelUp,
        gameProgress: {
          ...state.gameProgress,
          [gameType]: {
            ...currentProgress,
            level: newLevel,
            correctCount: newCorrectCount,
            totalPlayed: newTotalPlayed,
            highScore: Math.max(
              currentProgress.highScore,
              state.totalScore + scoreGain
            ),
          },
        },
        gameHistory: [
          ...state.gameHistory,
          {
            gameType,
            question: state.currentQuestion,
            correct: true,
            timestamp: new Date().toISOString(),
          },
        ].slice(-100), // 只保留最近100條記錄
      };
    }

    case ACTION_TYPES.ANSWER_WRONG: {
      const { gameType } = action.payload;
      return {
        ...state,
        streak: 0,
        showError: true,
        gameProgress: {
          ...state.gameProgress,
          [gameType]: {
            ...state.gameProgress[gameType],
            totalPlayed: state.gameProgress[gameType].totalPlayed + 1,
          },
        },
      };
    }

    case ACTION_TYPES.USE_HINT:
      return {
        ...state,
        hintsUsed: state.hintsUsed + 1,
        showHint: true,
      };

    case ACTION_TYPES.HIDE_HINT:
      return {
        ...state,
        showHint: false,
      };

    case ACTION_TYPES.HIDE_LEVEL_UP:
      return {
        ...state,
        showLevelUp: false,
      };

    case ACTION_TYPES.CLEAR_RESULT:
      return {
        ...state,
        showSuccess: false,
        showError: false,
      };

    case ACTION_TYPES.SHOW_FEEDBACK:
      return {
        ...state,
        feedback: action.payload,
      };

    case ACTION_TYPES.HIDE_FEEDBACK:
      return {
        ...state,
        feedback: null,
      };

    case ACTION_TYPES.LOAD_PROGRESS: {
      // 保存進度到 localStorage
      const newState = {
        ...state,
        ...action.payload,
        isLoading: false,
      };
      saveStateToStorage(state.user, action.payload);
      return newState;
    }

    case ACTION_TYPES.SAVE_SUCCESS:
      return {
        ...state,
        lastSaved: new Date().toISOString(),
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTION_TYPES.UNLOCK_ANIMAL:
      if (state.unlockedAnimals.includes(action.payload)) {
        return state;
      }
      return {
        ...state,
        unlockedAnimals: [...state.unlockedAnimals, action.payload],
      };

    case ACTION_TYPES.UPDATE_USER:
      // 直接更新狀態，不保存到 localStorage
      return {
        ...state,
        user: action.payload,
      };

    default:
      return state;
  }
}

// Context
const GameContext = createContext(null);

// Provider 組件
export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const saveTimeoutRef = useRef(null);
  const lastSaveDataRef = useRef(null);
  const initialLoadRef = useRef(false);

  // 啟動時從 localStorage 恢復登入狀態
  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    const savedState = loadSavedState();
    if (savedState && savedState.user) {
      // 恢復用戶登入狀態
      dispatch({
        type: ACTION_TYPES.LOGIN,
        payload: savedState.user,
      });

      // 如果有保存的進度，先恢復本地進度
      if (savedState.progress) {
        dispatch({
          type: ACTION_TYPES.LOAD_PROGRESS,
          payload: savedState.progress,
        });
      }

      // 嘗試從服務器同步最新進度
      MathZooAPI.loadProgress(savedState.user.id)
        .then((response) => {
          if (response.status === "success" && response.data) {
            dispatch({
              type: ACTION_TYPES.LOAD_PROGRESS,
              payload: response.data,
            });
          }
        })
        .catch((error) => {
          console.warn(
            "Failed to sync progress from server, using local data:",
            error
          );
        });
    }
  }, []);

  // 從MySQL加載進度（當遊戲開始時）
  const loadProgressFromServer = useCallback(async (userId) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
      const response = await MathZooAPI.loadProgress(userId);

      if (response.status === "success" && response.data) {
        dispatch({
          type: ACTION_TYPES.LOAD_PROGRESS,
          payload: response.data,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load progress from server:", error);
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });
      return false;
    }
  }, []);

  // 保存進度到MySQL（當遊戲狀態改變時）
  const saveProgressToServer = useCallback(async (userId, progressData) => {
    try {
      const response = await MathZooAPI.saveProgress(userId, progressData);
      if (response.status === "success") {
        dispatch({ type: ACTION_TYPES.SAVE_SUCCESS });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to save progress to server:", error);
      return false;
    }
  }, []);

  // 記錄遊戲歷史到MySQL
  const recordGameToServer = useCallback(async (userId, gameData) => {
    try {
      await MathZooAPI.recordGame(userId, gameData);
    } catch (error) {
      console.error("Failed to record game to server:", error);
    }
  }, []);

  // 登入 - 使用MySQL存儲
  const login = useCallback(async (username, password) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

      // 嘗試從MySQL加載用戶數據和進度
      const response = await MathZooAPI.login(username, password);

      if (response.status === "success" && response.data) {
        const { user, progress } = response.data;

        dispatch({
          type: ACTION_TYPES.LOGIN,
          payload: user,
        });

        // 加載進度（如果無存檔數據，後端會初始化默認狀態）
        if (progress) {
          dispatch({
            type: ACTION_TYPES.LOAD_PROGRESS,
            payload: progress,
          });
        }

        return { success: true, user, isNewUser: response.isNewUser || false };
      }

      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
      return { success: false, error: "登入失敗" };
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });

      // 如果是401錯誤，密碼錯誤
      if (error.response?.status === 401) {
        return {
          success: false,
          error: error.response?.data?.error || "密碼錯誤",
        };
      }

      return { success: false, error: error.message };
    }
  }, []);

  // 註冊新用戶
  const register = useCallback(
    async (username, password, parentUserId = null) => {
      try {
        dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

        const response = await MathZooAPI.register(
          username,
          password,
          parentUserId
        );

        if (response.status === "success" && response.data) {
          const { user, progress } = response.data;

          dispatch({
            type: ACTION_TYPES.LOGIN,
            payload: user,
          });

          if (progress) {
            dispatch({
              type: ACTION_TYPES.LOAD_PROGRESS,
              payload: progress,
            });
          }

          return { success: true, user };
        }

        dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
        return { success: false, error: "註冊失敗" };
      } catch (error) {
        dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });

        if (error.response?.status === 409) {
          return { success: false, error: "用戶名已被使用" };
        }

        return { success: false, error: error.message };
      }
    },
    []
  );

  // 登出 - 保存到MySQL後登出
  const logout = useCallback(async () => {
    if (state.user && state.user.id) {
      // 保存最終進度到MySQL
      const progressData = {
        totalScore: state.totalScore,
        totalStars: state.totalStars,
        streak: state.streak,
        gameProgress: state.gameProgress,
        unlockedAnimals: state.unlockedAnimals,
      };

      await saveProgressToServer(state.user.id, progressData);
    }
    dispatch({ type: ACTION_TYPES.LOGOUT });
  }, [
    state.user,
    state.totalScore,
    state.totalStars,
    state.streak,
    state.gameProgress,
    state.unlockedAnimals,
    saveProgressToServer,
  ]);

  // 開始遊戲
  const startGame = useCallback((gameType) => {
    dispatch({ type: ACTION_TYPES.START_GAME, payload: gameType });
  }, []);

  // 結束遊戲
  const endGame = useCallback(() => {
    dispatch({ type: ACTION_TYPES.END_GAME });
  }, []);

  // 設置問題
  const setQuestion = useCallback((question) => {
    dispatch({ type: ACTION_TYPES.SET_QUESTION, payload: question });
  }, []);

  // 答對 - 同時記錄到MySQL
  const answerCorrect = useCallback(
    (
      gameType,
      usedHint = false,
      scoreGained = 0,
      starsGained = 0,
      question = null
    ) => {
      dispatch({
        type: ACTION_TYPES.ANSWER_CORRECT,
        payload: { gameType, usedHint },
      });

      // 記錄遊戲歷史到MySQL
      if (state.user?.id) {
        recordGameToServer(state.user.id, {
          gameType,
          question,
          correct: true,
          usedHint,
          scoreGained,
          starsGained,
          timeSpent: 0, // 可以添加計時功能
        });
      }
    },
    [state.user, recordGameToServer]
  );

  // 答錯 - 同時記錄到MySQL
  const answerWrong = useCallback(
    (gameType, question = null) => {
      dispatch({
        type: ACTION_TYPES.ANSWER_WRONG,
        payload: { gameType },
      });

      // 記錄遊戲歷史到MySQL
      if (state.user?.id) {
        recordGameToServer(state.user.id, {
          gameType,
          question,
          correct: false,
          usedHint: false,
          scoreGained: 0,
          starsGained: 0,
          timeSpent: 0,
        });
      }
    },
    [state.user, recordGameToServer]
  );

  // 使用提示
  const useHint = useCallback(() => {
    dispatch({ type: ACTION_TYPES.USE_HINT });
  }, []);

  // 隱藏提示
  const hideHint = useCallback(() => {
    dispatch({ type: ACTION_TYPES.HIDE_HINT });
  }, []);

  // 隱藏升級動畫
  const hideLevelUp = useCallback(() => {
    dispatch({ type: ACTION_TYPES.HIDE_LEVEL_UP });
  }, []);

  // 清除結果狀態
  const clearResult = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_RESULT });
  }, []);

  // 顯示反饋
  const showFeedback = useCallback((message, type = "info") => {
    dispatch({
      type: ACTION_TYPES.SHOW_FEEDBACK,
      payload: { message, type },
    });
    setTimeout(() => {
      dispatch({ type: ACTION_TYPES.HIDE_FEEDBACK });
    }, 3000);
  }, []);

  // 解鎖動物
  const unlockAnimal = useCallback((animalId) => {
    dispatch({ type: ACTION_TYPES.UNLOCK_ANIMAL, payload: animalId });
  }, []);

  // 獲取當前遊戲等級
  const getCurrentLevel = useCallback(
    (gameType) => {
      return state.gameProgress[gameType]?.level || 1;
    },
    [state.gameProgress]
  );

  // 更新用戶資料 - 直接從數據庫獲取和保存
  const updateUser = useCallback(async (userData) => {
    try {
      if (!userData.id) {
        throw new Error("用戶ID不存在");
      }

      // 先更新數據庫
      const response = await MathZooAPI.updateUser(userData.id, {
        avatar: userData.avatar,
        username: userData.username,
      });

      if (response.status === "success") {
        // 數據庫更新成功後，重新獲取用戶資料
        const userResponse = await MathZooAPI.getUser(userData.id);
        if (userResponse.status === "success" && userResponse.data) {
          // 更新本地狀態
          dispatch({
            type: ACTION_TYPES.UPDATE_USER,
            payload: userResponse.data,
          });
        } else {
          // 如果無法獲取最新資料，使用傳入的資料
          dispatch({ type: ACTION_TYPES.UPDATE_USER, payload: userData });
        }
        return { success: true };
      } else {
        throw new Error(response.error || "更新失敗");
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      return { success: false, error: error.message };
    }
  }, []);

  // 自動保存進度到 localStorage 和 MySQL（每次進度變化時）
  useEffect(() => {
    if (!state.isLoggedIn || !state.user?.id) return;

    const progressData = {
      totalScore: state.totalScore,
      totalStars: state.totalStars,
      streak: state.streak,
      gameProgress: state.gameProgress,
      unlockedAnimals: state.unlockedAnimals,
    };

    // 保存到 localStorage
    saveStateToStorage(state.user, progressData);

    // 檢查數據是否有變化，避免不必要的保存到服務器
    const currentDataStr = JSON.stringify(progressData);
    if (lastSaveDataRef.current !== currentDataStr) {
      // 如果升級了，立即保存；否則延遲2秒防抖
      if (state.showLevelUp) {
        // 升級時立即保存到服務器
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveProgressToServer(state.user.id, progressData).then((success) => {
          if (success) {
            lastSaveDataRef.current = currentDataStr;
            console.log("🎉 Level up! Progress saved immediately.");
          }
        });
      } else {
        // 一般情況：延遲2秒保存，避免頻繁請求
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(async () => {
          const success = await saveProgressToServer(
            state.user.id,
            progressData
          );
          if (success) {
            lastSaveDataRef.current = currentDataStr;
          }
        }, 2000);
      }
    }
  }, [
    state.isLoggedIn,
    state.user,
    state.totalScore,
    state.totalStars,
    state.streak,
    state.gameProgress,
    state.unlockedAnimals,
    state.showLevelUp,
    saveProgressToServer,
  ]);

  // 自動保存到MySQL（每30秒）
  useEffect(() => {
    if (!state.isLoggedIn || !state.user?.id) return;

    const saveInterval = setInterval(async () => {
      const progressData = {
        totalScore: state.totalScore,
        totalStars: state.totalStars,
        streak: state.streak,
        gameProgress: state.gameProgress,
        unlockedAnimals: state.unlockedAnimals,
      };

      // 檢查數據是否有變化，避免不必要的保存
      const currentDataStr = JSON.stringify(progressData);
      if (lastSaveDataRef.current === currentDataStr) {
        return; // 數據沒有變化，跳過保存
      }

      const success = await saveProgressToServer(state.user.id, progressData);
      if (success) {
        lastSaveDataRef.current = currentDataStr;
      }
    }, AUTO_SAVE_INTERVAL); // 每30秒自動保存

    return () => clearInterval(saveInterval);
  }, [
    state.isLoggedIn,
    state.user,
    state.totalScore,
    state.totalStars,
    state.streak,
    state.gameProgress,
    state.unlockedAnimals,
    saveProgressToServer,
  ]);

  // 檢查成就解鎖
  useEffect(() => {
    const starThresholds = {
      10: "cat",
      25: "dog",
      50: "panda",
      100: "penguin",
      200: "monkey",
      500: "elephant",
      1000: "owl",
    };

    Object.entries(starThresholds).forEach(([threshold, animalId]) => {
      if (
        state.totalStars >= parseInt(threshold) &&
        !state.unlockedAnimals.includes(animalId)
      ) {
        unlockAnimal(animalId);
        showFeedback(`🎉 恭喜！你解鎖了新動物：${animalId}！`, "success");
      }
    });
  }, [state.totalStars, state.unlockedAnimals, unlockAnimal, showFeedback]);

  const value = {
    state,
    actions: {
      login,
      logout,
      register,
      loadProgressFromServer,
      saveProgressToServer,
      startGame,
      endGame,
      setQuestion,
      answerCorrect,
      answerWrong,
      useHint,
      hideHint,
      hideLevelUp,
      clearResult,
      showFeedback,
      unlockAnimal,
      getCurrentLevel,
      updateUser,
    },
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// Hook
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGame must be used within a GameProvider");
  }
  return context;
}

export default GameContext;
